import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-6a3a209c"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "message-content" }
const _hoisted_2 = {
  key: 0,
  class: "message-icon"
}
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = {
  key: 1,
  class: "message-media"
}
const _hoisted_5 = ["src", "alt"]
const _hoisted_6 = {
  key: 1,
  class: "video-embed-container"
}
const _hoisted_7 = ["src"]
const _hoisted_8 = ["src", "controls", "autoplay"]
const _hoisted_9 = {
  key: 2,
  class: "question-input"
}
const _hoisted_10 = ["placeholder", "onKeydown"]
const _hoisted_11 = ["placeholder"]
const _hoisted_12 = { class: "question-buttons" }
const _hoisted_13 = ["disabled"]
const _hoisted_14 = {
  key: 3,
  class: "message-actions"
}
const _hoisted_15 = ["onClick"]

import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import type { PecTutorConfig } from '../types';

// Props
interface Props {
  message: {
    text?: string;
    type?: string;
    duration?: number;
    position?: string;
    dismissible?: boolean;
    media?: {
      type: string;
      src: string;
      caption?: string;
      controls?: boolean;
      autoplay?: boolean;
    };
    isQuestion?: boolean;
    placeholder?: string;
    required?: boolean;
    multiline?: boolean;
    validationFn?: (input: string) => boolean | string;
    actions?: Array<{
      id: string;
      label: string;
      type?: string;
    }>;
  };
  config: PecTutorConfig;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'MessageBox',
  props: {
    message: {},
    config: {}
  },
  emits: ["close", "response", "action"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// Emits
const emit = __emit;

// Reactive state
const userResponse = ref('');
const shouldShake = ref(false);
const timeoutId = ref<number | null>(null);
const isPaused = ref(false);
const startTime = ref(0);
const remainingTime = ref(0);

// Computed properties
const messageIcon = computed(() => {
  const icons: Record<string, string> = {
    normal: '',
    attention: '⚠️',
    warning: '⚠️',
    tip: '💡',
    success: '✅',
    error: '❌',
    question: '❓'
  };
  return icons[props.message?.type || 'normal'] || '';
});

const isYoutubeVideo = computed(() => {
  return props.message.media?.type === 'video' && props.message.media.src.includes('youtube.com/embed');
});

const formattedText = computed(() => {
  if (!props.message?.text) return '';
  
  // Simple markdown-like formatting
  return props.message.text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`(.*?)`/g, '<code>$1</code>')
    .replace(/\n/g, '<br>');
});

  const messageBoxStyles = computed(() => {
    const position = props.message?.position || 'center';
    
    const positions: Record<string, Record<string, string>> = {
    top: { top: '20px', left: '50%', transform: 'translateX(-50%)' },
    center: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
    bottom: { bottom: '20px', left: '50%', transform: 'translateX(-50%)' },
    'follow-mouse': { /* Will be handled by mouse position */ }
  };

  return {
    ...positions[position],
    maxWidth: '90vw',
    width: 'auto',
  };
});

// Methods
const handleClose = () => {
  clearAutoClose();
  emit('close');
};

const handleOverlayClick = () => {
  if (props.message?.dismissible !== false) {
    handleClose();
  }
};

const submitResponse = () => {
  if (props.message?.required !== false && !userResponse.value.trim()) {
    shake();
    return;
  }

  // Validate if validation function exists
  if (props.message?.validationFn) {
    const validation = props.message.validationFn(userResponse.value);
    if (validation !== true) {
      shake();
      // Show validation error if it's a string
      if (typeof validation === 'string') {
        console.warn('Validation error:', validation);
      }
      return;
    }
  }

  emit('response', userResponse.value);
  userResponse.value = '';
};

const skipQuestion = () => {
  emit('response', '');
  userResponse.value = '';
};

const handleAction = (action: { id: string; label: string; type?: string }) => {
  emit('action', action);
};

const shake = () => {
  shouldShake.value = true;
  setTimeout(() => {
    shouldShake.value = false;
  }, props.config.timeouts.attentionShake);
};

const setupAutoClose = () => {
  if (props.message?.duration && props.message?.type !== 'question') {
    startTimer(props.message.duration);
  }
};

const startTimer = (duration: number) => {
  clearAutoClose();
  startTime.value = Date.now();
  remainingTime.value = duration;
  timeoutId.value = window.setTimeout(() => {
    handleClose();
  }, duration);
};

const clearAutoClose = () => {
  if (timeoutId.value) {
    clearTimeout(timeoutId.value);
    timeoutId.value = null;
  }
};

const pauseTimer = () => {
  if (isPaused.value || !timeoutId.value) return;
  isPaused.value = true;
  clearAutoClose();
  const elapsedTime = Date.now() - startTime.value;
  remainingTime.value -= elapsedTime;
};

const resumeTimer = () => {
  if (!isPaused.value) return;
  isPaused.value = false;
  if (remainingTime.value > 0) {
    startTimer(remainingTime.value);
  }
};

// Watch for message changes
watch(() => props.message, (newMessage) => {
  clearAutoClose();
  userResponse.value = '';
  isPaused.value = false;
  
  if (newMessage) {
    setupAutoClose();
    
    // Handle attention type
    if (newMessage.type === 'attention') {
      shake();
    }
  }
}, { immediate: true });

// Lifecycle
onMounted(() => {
  // setupAutoClose is now called from the watcher
});

onUnmounted(() => {
  clearAutoClose();
});

return (_ctx: any,_cache: any) => {
  return (_ctx.message)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(["message-box-overlay", { 'overlay-dismissible': _ctx.message.dismissible !== false }]),
        onClick: handleOverlayClick
      }, [
        _createElementVNode("div", {
          class: _normalizeClass(["message-box", [
        `message-${_ctx.message.type || 'normal'}`,
        { 'message-shake': shouldShake.value }
      ]]),
          style: _normalizeStyle(messageBoxStyles.value),
          onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"])),
          onMousedown: pauseTimer,
          onMouseup: resumeTimer,
          onMouseleave: resumeTimer
        }, [
          (_ctx.message.dismissible !== false)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                class: "close-button",
                onClick: handleClose,
                "aria-label": "Close message"
              }, " × "))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_1, [
            (messageIcon.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(messageIcon.value), 1))
              : _createCommentVNode("", true),
            _createElementVNode("div", {
              class: "message-text",
              innerHTML: formattedText.value
            }, null, 8, _hoisted_3),
            (_ctx.message.media)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  (_ctx.message.media.type === 'image')
                    ? (_openBlock(), _createElementBlock("img", {
                        key: 0,
                        src: _ctx.message.media.src,
                        alt: _ctx.message.media.caption || 'Tutorial image',
                        class: "media-image"
                      }, null, 8, _hoisted_5))
                    : (isYoutubeVideo.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                          _createElementVNode("iframe", {
                            src: _ctx.message.media.src,
                            frameborder: "0",
                            allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
                            allowfullscreen: "",
                            class: "media-video-embed"
                          }, null, 8, _hoisted_7)
                        ]))
                      : (_ctx.message.media.type === 'video')
                        ? (_openBlock(), _createElementBlock("video", {
                            key: 2,
                            src: _ctx.message.media.src,
                            controls: _ctx.message.media.controls !== false,
                            autoplay: _ctx.message.media.autoplay || false,
                            class: "media-video"
                          }, null, 8, _hoisted_8))
                        : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true),
            (_ctx.message.isQuestion)
              ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  (_ctx.message.multiline)
                    ? _withDirectives((_openBlock(), _createElementBlock("textarea", {
                        key: 0,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((userResponse).value = $event)),
                        placeholder: _ctx.message.placeholder || 'Type your answer...',
                        class: "input-field input-textarea",
                        rows: "3",
                        onKeydown: _withKeys(_withModifiers(submitResponse, ["ctrl"]), ["enter"])
                      }, null, 40, _hoisted_10)), [
                        [_vModelText, userResponse.value]
                      ])
                    : _withDirectives((_openBlock(), _createElementBlock("input", {
                        key: 1,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((userResponse).value = $event)),
                        type: "text",
                        placeholder: _ctx.message.placeholder || 'Type your answer...',
                        class: "input-field input-text",
                        onKeydown: _withKeys(submitResponse, ["enter"])
                      }, null, 40, _hoisted_11)), [
                        [_vModelText, userResponse.value]
                      ]),
                  _createElementVNode("div", _hoisted_12, [
                    _createElementVNode("button", {
                      class: "btn btn-primary",
                      onClick: submitResponse,
                      disabled: !userResponse.value.trim() && _ctx.message.required !== false
                    }, " Submit ", 8, _hoisted_13),
                    (!_ctx.message.required)
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 0,
                          class: "btn btn-secondary",
                          onClick: skipQuestion
                        }, " Skip "))
                      : _createCommentVNode("", true)
                  ])
                ]))
              : _createCommentVNode("", true),
            (_ctx.message.actions)
              ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.message.actions, (action) => {
                    return (_openBlock(), _createElementBlock("button", {
                      key: action.id,
                      class: _normalizeClass(["btn", `btn-${action.type || 'primary'}`]),
                      onClick: ($event: any) => (handleAction(action))
                    }, _toDisplayString(action.label), 11, _hoisted_15))
                  }), 128))
                ]))
              : _createCommentVNode("", true)
          ]),
          (_ctx.message.duration && _ctx.message.type !== 'question')
            ? (_openBlock(), _createElementBlock("div", {
                key: 1,
                class: _normalizeClass(["progress-bar", { 'paused': isPaused.value }])
              }, [
                _createElementVNode("div", {
                  class: "progress-fill",
                  style: _normalizeStyle({ animationDuration: `${_ctx.message.duration}ms` })
                }, null, 4)
              ], 2))
            : _createCommentVNode("", true)
        ], 38)
      ], 2))
    : _createCommentVNode("", true)
}
}

})